import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { WandSparkles } from 'lucide-react';
import type {
  QuestionConceptRef,
  QuestionListItem,
  QuestionPurpose,
  QuestionTaxonomyTag,
  QuestionTypePurposeCount,
} from '@course-authoring/shared-types';
import { AdminStatusMessage, useAdminNarrowViewport } from '../../admin-ui';
import { isAuthorWithAiQuestionType, type AuthorWithAiQuestionType } from '../../api/authorWithAiApi';
import { buildErrorMessage } from '../../api/authorWithAiBuildErrors';
import { useAuthorWithAiAvailability, useCreateBuild } from '../../api/authorWithAiQueries';
import type { LibraryConcept } from '../../api/centerstageApi';
import type { QuestionBrowseScope, QuestionListFilters } from '../../api/questionApi';
import { useQuestionBrowseFacets, useQuestionBrowsePage } from '../../api/questionBrowseQueries';
import { CenterstageConceptsModal, type ConceptsModalCopy } from '../../components/centerstage/CenterstageConceptsModal';
import type { CenterstageFilterChip } from '../../components/centerstage/CenterstageFilterChips';
import { QuestionBankCards } from '../../components/questionBankBrowse/QuestionBankCards';
import { QuestionBankDetail } from '../../components/questionBankBrowse/QuestionBankDetail';
import { QuestionBankFindRow } from '../../components/questionBankBrowse/QuestionBankFindRow';
import { QuestionBankSortRow, sortOptionFor } from '../../components/questionBankBrowse/QuestionBankSortRow';
import { QUESTION_BANK_BROWSE_COPY as COPY } from '../../components/questionBankBrowse/questionBankBrowseCopy';
import { indexTaxonomyLabels, orderLabelIdsForChips } from '../../components/questionBankBrowse/questionBankLabels';
import { createQuestionBankBrowseStyles, type QuestionBankBrowseStyles } from '../../components/questionBankBrowse/questionBankBrowseStyles';
import { groupKey } from '../../components/questionBankBrowse/QuestionBankTaxonomySections';
import { useClientContext } from '../../hooks/useClientContext';
import { usePageBoundsReset } from '../../hooks/useListControls';
import { QuestionBankTaxonomyPanel } from './QuestionBankTaxonomyPanel';
import { defaultPurposeFor, QUESTION_BANK_OPENING, useQuestionBankFilters } from './useQuestionBankFilters';

const PURPOSE_ORDER: readonly QuestionPurpose[] = ['Sentiment', 'Knowledge', 'Reflection', 'Survey'];

/** The purposes the scope's bank holds for a type, in the order Sentiment, Knowledge, Reflection. */
export const purposesFor = (pairs: readonly QuestionTypePurposeCount[], blockType: AuthorWithAiQuestionType): QuestionPurpose[] =>
  PURPOSE_ORDER.filter((purpose) => pairs.some((pair) => pair.blockType === blockType && pair.purpose === purpose));

const CONCEPTS_COPY: ConceptsModalCopy = {
  title: COPY.conceptsTitle,
  countsLoading: COPY.countsLoading,
  countsError: COPY.countsError,
  noItems: COPY.noQuestionsForConcept,
};

interface StartLessonFooterProps {
  styles: QuestionBankBrowseStyles;
  item: QuestionListItem;
  /** The content context the build is created in (null for Standard). */
  accountId: number | null;
}

/**
 * "Start a new lesson with this question": creates an Author with AI build
 * from the question (the state a chat pick of it reaches) and opens its
 * conversation; a refusal shows in the dialog.
 */
function StartLessonFooter({ styles, item, accountId }: StartLessonFooterProps) {
  const navigate = useNavigate();
  const create = useCreateBuild();
  const creating = useRef(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const wasPending = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const c = styles.centerstage;

  // The button is disabled while the build is created, which drops focus; after a refusal,
  // take focus back so Escape still reaches the dialog.
  useEffect(() => {
    if (wasPending.current && !create.isPending) {
      const dialog = buttonRef.current?.closest('[role="dialog"]');
      if (dialog && !dialog.contains(document.activeElement)) buttonRef.current?.focus();
    }
    wasPending.current = create.isPending;
  }, [create.isPending]);

  const start = () => {
    if (creating.current || create.isPending) return;
    creating.current = true;
    setError(null);
    create.mutate({ accountId, source: { kind: 'question', libraryId: String(item.questionDefinitionId) } }, {
      onSettled: () => { creating.current = false; },
      onSuccess: (response) => navigate(`/author-with-ai/builds/${response.buildId}`),
      onError: (refusal) => setError(buildErrorMessage(refusal, COPY.startError)),
    });
  };

  return (
    <>
      {error ? <AdminStatusMessage tone="error" style={c.footerError}>{error}</AdminStatusMessage> : null}
      <button
        ref={buttonRef}
        type="button"
        onClick={start}
        disabled={create.isPending}
        style={{ ...c.buildButton, ...(create.isPending ? c.buttonDisabled : {}) }}
      >
        {/* The Author with AI glyph, as in the nav rail and the Centerstage dialog. */}
        <WandSparkles size="1em" strokeWidth={1.75} aria-hidden="true" />
        {create.isPending ? COPY.startingLesson : COPY.startLesson}
      </button>
    </>
  );
}

/** Scrolls the page's scroll container (the shell's content pane, else the window) to its top. */
function scrollPageToTop(from: HTMLElement | null) {
  let node = from?.parentElement ?? null;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) break;
    node = node.parentElement;
  }
  (node ?? document.scrollingElement)?.scrollTo?.({ top: 0 });
}

/**
 * The Question Bank page (Sara's "Question Bank — Developer Handoff",
 * 2026-11-03): the bank in the current content context's scope (Standard,
 * or Standard plus the account's bank), browsable by search, concept, Type
 * and Purpose, framework labels and four sorts, held in the URL; a detail
 * dialog hands a question to Author with AI. Nothing is edited here; the
 * management screen is at /questions/manage.
 */
export function QuestionBankPage() {
  const narrow = useAdminNarrowViewport();
  const styles = useMemo(() => {
    const base = createQuestionBankBrowseStyles({ narrow });
    // On the page the search field takes the row's spare width; Type and Purpose keep their size (writeup).
    return { ...base, centerstage: { ...base.centerstage, findBar: { ...base.centerstage.findBar, flex: '1 1 260px', minWidth: 0 } } };
  }, [narrow]);
  const b = styles.browse;
  const c = styles.centerstage;
  const { filters, update } = useQuestionBankFilters();
  const { clientContext } = useClientContext();
  const accountId = clientContext ? Number(clientContext.id) : null;
  const availability = useAuthorWithAiAvailability();

  const scope: QuestionBrowseScope = useMemo(() => ({ externalAccountId: accountId, includeStandard: accountId !== null }), [accountId]);
  const facets = useQuestionBrowseFacets(scope);
  const facetsSettled = facets.isSuccess || facets.isError;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);
  const [conceptsOpen, setConceptsOpen] = useState(false);
  const [detail, setDetail] = useState<QuestionListItem | null>(null);
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(() => new Set());
  // Bumped when the control that had focus goes away (a removed chip, "Show all questions",
  // a label link whose card is disabled while the list reloads): focus moves to the search field.
  const [focusSearch, setFocusSearch] = useState(0);
  const takeFocus = () => setFocusSearch((tick) => tick + 1);
  useEffect(() => {
    if (focusSearch === 0) return;
    topRef.current?.querySelector<HTMLElement>('input[role="combobox"]')?.focus();
  }, [focusSearch]);

  // The field shows the committed term whenever it changes (Enter, a chip, back, Show all).
  const [draft, setDraft] = useState(filters.q);
  useEffect(() => {
    setDraft(filters.q);
  }, [filters.q]);
  // A context switch clears the filters (useQuestionBankFilters), anything typed and the panel.
  const contextId = clientContext?.id ?? null;
  const previousContextId = useRef(contextId);
  useEffect(() => {
    if (previousContextId.current === contextId) return;
    previousContextId.current = contextId;
    setDraft('');
    setOpenGroups(new Set());
    setDetail(null);
  }, [contextId]);

  // Purpose offers what the bank holds for the type; until the facets answer it shows the current value.
  const pairs = facets.data?.typePurposes;
  const offered = useMemo(() => (pairs ? purposesFor(pairs, filters.blockType) : null), [pairs, filters.blockType]);
  const purpose: QuestionPurpose = offered === null
    ? filters.purpose
    : offered.length === 0
      ? defaultPurposeFor(filters.blockType)
      : offered.includes(filters.purpose) ? filters.purpose : offered[0]!;
  const purposeChoices = offered && offered.length > 1 ? offered : [purpose];
  const sentiment = purpose === 'Sentiment';

  // A linked purpose the type does not offer is replaced, not stacked onto history.
  useEffect(() => {
    if (!offered || offered.length === 0 || offered.includes(filters.purpose)) return;
    update({ purpose: offered[0]!, ...(offered[0] !== 'Sentiment' ? { labelIds: [] } : {}) }, { replace: true });
  }, [offered, filters.purpose, update]);
  // Only Sentiment questions carry framework tags: away from Sentiment no label applies and the groups close.
  useEffect(() => {
    if (sentiment) return;
    setOpenGroups((current) => (current.size === 0 ? current : new Set()));
    if (filters.labelIds.length > 0) update({ labelIds: [] }, { replace: true });
  }, [sentiment, filters.labelIds.length, update]);

  const sortOption = sortOptionFor(filters.sortKey);
  const listFilters: QuestionListFilters = useMemo(() => ({
    blockType: filters.blockType,
    purpose,
    sort: sortOption.sort,
    order: sortOption.order,
    page: filters.page,
    pageSize: filters.pageSize,
    ...(filters.q ? { search: filters.q } : {}),
    ...(filters.conceptId !== null ? { conceptId: filters.conceptId } : {}),
    ...(sentiment && filters.labelIds.length > 0 ? { taxonomyLabelIds: filters.labelIds } : {}),
    ...(accountId !== null ? { externalAccountId: accountId, includeStandard: true } : {}),
  }), [filters, purpose, sortOption, sentiment, accountId]);
  const list = useQuestionBrowsePage(listFilters);
  const setPageInPlace = useCallback((page: number) => update({ page }, { replace: true }), [update]);
  // A result set that shrinks under the current page lands on its last page, not on an empty one.
  // Nothing is known before the first answer, so a linked page is kept until then.
  usePageBoundsReset({
    page: filters.page,
    pageSize: filters.pageSize,
    totalCount: list.data?.totalCount ?? 0,
    isPlaceholderData: list.isPlaceholderData || !list.data,
    setPage: setPageInPlace,
  });

  const concepts = useMemo(() => facets.data?.concepts ?? [], [facets.data]);
  const categories = useMemo(() => facets.data?.categories ?? [], [facets.data]);
  const categoryNames = useMemo(() => new Map(categories.map((category) => [category.id, category.name])), [categories]);
  // The autosuggest and the browse modal offer only concepts that lead somewhere.
  const suggestable: LibraryConcept[] = useMemo(
    () => concepts.filter((concept) => concept.questionCount > 0).map((concept) => ({ id: concept.id, name: concept.name, categoryId: concept.categoryId })),
    [concepts],
  );
  const counts = useMemo(() => Object.fromEntries(concepts.map((concept) => [concept.id, concept.questionCount])), [concepts]);
  const labels = useMemo(() => indexTaxonomyLabels(facets.data?.taxonomies ?? []), [facets.data]);

  const pickConcept = (conceptId: number) => {
    setDraft('');
    setConceptsOpen(false);
    update({ conceptId, q: '' });
  };
  const commitSearch = (term: string) => {
    setDraft(term);
    update({ q: term, conceptId: null });
  };
  const changeType = (next: AuthorWithAiQuestionType) => {
    const nextOffered = pairs ? purposesFor(pairs, next) : [];
    const nextPurpose = nextOffered[0] ?? defaultPurposeFor(next);
    update({ blockType: next, purpose: nextPurpose, ...(nextPurpose !== 'Sentiment' ? { labelIds: [] } : {}) });
  };
  const changePurpose = (next: QuestionPurpose) => {
    update({ purpose: next, ...(next !== 'Sentiment' ? { labelIds: [] } : {}) });
  };
  const toggleLabel = (labelId: number) => {
    update({ labelIds: filters.labelIds.includes(labelId) ? filters.labelIds.filter((id) => id !== labelId) : [...filters.labelIds, labelId] });
  };
  const setGroups = (keys: readonly string[], open: boolean) => {
    setOpenGroups((current) => {
      const next = new Set(current);
      for (const key of keys) {
        if (open) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  };
  // Clears the search, concept and labels and returns Type and Purpose to the opening state; the sort stays (writeup, prototype).
  const showAll = () => {
    setDraft('');
    update({
      q: '',
      conceptId: null,
      labelIds: [],
      blockType: QUESTION_BANK_OPENING.blockType,
      purpose: QUESTION_BANK_OPENING.purpose,
    });
    takeFocus();
  };
  const jumpToLabel = (tag: QuestionTaxonomyTag) => {
    setDetail(null);
    setDraft('');
    update({
      q: '',
      conceptId: null,
      labelIds: [tag.labelId],
      ...(sentiment ? {} : { blockType: 'likert-scale' as const, purpose: 'Sentiment' as const }),
    });
    setGroups([groupKey(tag.taxonomyId, tag.parentLabel)], true);
    takeFocus();
  };
  // The detail's concept link: that concept alone, the search and every label cleared; Type and Purpose stay.
  const jumpToConcept = (concept: QuestionConceptRef) => {
    setDetail(null);
    setDraft('');
    update({ q: '', conceptId: concept.id, labelIds: [] });
    takeFocus();
  };

  const chips: CenterstageFilterChip[] = [];
  if (filters.q) chips.push({ key: 'search', label: `“${filters.q}”`, onRemove: () => { setDraft(''); update({ q: '' }); takeFocus(); } });
  // Names come from the facets; until they answer there is nothing to label a concept or label chip with.
  if (facetsSettled && filters.conceptId !== null) {
    const conceptId = filters.conceptId;
    chips.push({
      key: 'concept',
      label: concepts.find((concept) => concept.id === conceptId)?.name ?? COPY.conceptFallback(conceptId),
      onRemove: () => { update({ conceptId: null }); takeFocus(); },
    });
  }
  if (facetsSettled && sentiment) {
    for (const labelId of orderLabelIdsForChips(filters.labelIds, labels)) {
      chips.push({
        key: `label-${labelId}`,
        label: labels.get(labelId)?.childLabel ?? COPY.labelFallback(labelId),
        onRemove: () => { update({ labelIds: filters.labelIds.filter((id) => id !== labelId) }); takeFocus(); },
      });
    }
  }

  const total = list.data?.totalCount ?? 0;
  const items = (list.data?.items ?? []).filter((item) => isAuthorWithAiQuestionType(item.blockType));
  const status: 'pending' | 'error' | 'success' = list.isPending ? 'pending' : list.isError && !list.data ? 'error' : 'success';

  return (
    <div ref={rootRef} style={c.page}>
      <div style={c.wrap}>
        <div ref={topRef} style={c.sticky}>
          <div style={b.pageHeader}>
            <div style={c.titleBlock}>
              <h1 style={c.title}>{COPY.title}</h1>
              <p style={c.description}>{COPY.pageDescription}</p>
            </div>
          </div>
          <QuestionBankFindRow
            styles={styles}
            draft={draft}
            onDraftChange={setDraft}
            searchTerm={filters.q}
            onCommitSearch={commitSearch}
            concepts={suggestable}
            categoryNames={categoryNames}
            onPickConcept={pickConcept}
            onBrowseConcepts={() => setConceptsOpen(true)}
            searchPlaceholder={COPY.pageSearchPlaceholder}
            blockType={filters.blockType}
            onTypeChange={changeType}
            purposes={purposeChoices}
            purpose={purpose}
            onPurposeChange={changePurpose}
          />
          {facets.isError ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <AdminStatusMessage tone="error">{COPY.facetsError}</AdminStatusMessage>
              <button type="button" onClick={() => void facets.refetch()} style={c.secondaryButton}>{COPY.retry}</button>
            </div>
          ) : null}
          <QuestionBankSortRow
            styles={styles}
            variant="page"
            sortKey={filters.sortKey}
            onSortChange={(sortKey) => update({ sortKey })}
            chips={chips}
            total={total}
            page={filters.page}
            pageSize={filters.pageSize}
            onShowAll={showAll}
          />
        </div>
        <div style={b.pageBody}>
          <div style={b.listColumn}>
            <QuestionBankCards
              styles={styles}
              status={status}
              items={items}
              sortKey={filters.sortKey}
              stale={list.isPlaceholderData}
              total={total}
              page={filters.page}
              pageSize={filters.pageSize}
              onOpen={setDetail}
              onRetry={() => void list.refetch()}
              onPageChange={(page) => { update({ page }); scrollPageToTop(rootRef.current); }}
              onPageSizeChange={(pageSize) => { update({ pageSize }); scrollPageToTop(rootRef.current); }}
              foot={<Link to="/questions/manage" style={b.manageLink}>{COPY.manageQuestions}</Link>}
            />
          </div>
          <QuestionBankTaxonomyPanel
            styles={styles}
            taxonomies={facets.data?.taxonomies ?? []}
            purpose={purpose}
            checkedLabelIds={filters.labelIds}
            openGroups={openGroups}
            onSetGroups={setGroups}
            onToggleLabel={toggleLabel}
            onShowSentiment={() => update({ blockType: 'likert-scale', purpose: 'Sentiment' })}
          />
        </div>
      </div>

      {/* Siblings of the page content, so Escape and a backdrop click close only the innermost layer. */}
      <CenterstageConceptsModal
        styles={c}
        isOpen={conceptsOpen}
        categories={categories}
        concepts={suggestable}
        counts={facets.data ? counts : undefined}
        countsStatus={facets.isError ? 'error' : facets.data ? 'success' : 'pending'}
        onPick={pickConcept}
        onClose={() => setConceptsOpen(false)}
        copy={CONCEPTS_COPY}
      />
      <QuestionBankDetail
        styles={styles}
        item={detail}
        width="560px"
        footer={(item) => (availability.status === 'available'
          ? <StartLessonFooter styles={styles} item={item} accountId={accountId} />
          : null)}
        onClose={() => setDetail(null)}
        onFilterLabel={jumpToLabel}
        onFilterConcept={jumpToConcept}
      />
    </div>
  );
}
